/**
 * PWA Composable
 * Provides PWA installation, update, and status functionality
 */
import { ref, computed, onMounted } from 'vue'
import { useRegisterSW } from 'virtual:pwa-register/vue'

// Install prompt event
let deferredPrompt: BeforeInstallPromptEvent | null = null

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// Track if app is installable
const isInstallable = ref(false)
const isInstalled = ref(false)
const isOnline = ref(navigator.onLine)

// Listen for install prompt
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e as BeforeInstallPromptEvent
    isInstallable.value = true
    console.log('[PWA] App is installable')
  })

  // Track installation
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    isInstallable.value = false
    isInstalled.value = true
    console.log('[PWA] App was installed')
  })

  // Track online/offline status
  window.addEventListener('online', () => {
    isOnline.value = true
    console.log('[PWA] Online')
  })

  window.addEventListener('offline', () => {
    isOnline.value = false
    console.log('[PWA] Offline')
  })
}

export function usePWA() {
  const {
    needRefresh,
    offlineReady,
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(swUrl, registration) {
      console.log('[PWA] Service Worker registered:', swUrl)

      // Only check for updates in production (HMR handles dev updates)
      if (registration && !import.meta.env.DEV) {
        setInterval(() => {
          registration.update()
        }, 60 * 60 * 1000) // Check every hour in production
      }
    },
    onRegisterError(error) {
      console.error('[PWA] Service Worker registration error:', error)
    },
    onNeedRefresh() {
      console.log('[PWA] New content available, refresh needed')
    },
    onOfflineReady() {
      console.log('[PWA] App ready for offline use')
    },
  })

  // Check if running in standalone mode (installed PWA)
  const isStandalone = computed(() => {
    if (typeof window === 'undefined') return false
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    )
  })

  // Trigger app installation
  async function installApp(): Promise<boolean> {
    if (!deferredPrompt) {
      console.log('[PWA] No install prompt available')
      return false
    }

    try {
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice

      console.log('[PWA] Install prompt outcome:', outcome)

      if (outcome === 'accepted') {
        deferredPrompt = null
        isInstallable.value = false
        return true
      }
      return false
    } catch (error) {
      console.error('[PWA] Install error:', error)
      return false
    }
  }

  // Update the app (reload with new SW)
  async function updateApp() {
    await updateServiceWorker(true)
  }

  // Dismiss update notification
  function dismissUpdate() {
    needRefresh.value = false
  }

  /**
   * Set the app badge (notification count on app icon)
   * Supported on installed PWAs on Android, iOS, Windows, macOS
   */
  async function setBadge(count: number): Promise<boolean> {
    // Check if Badge API is supported
    if (!('setAppBadge' in navigator)) {
      console.log('[PWA] Badge API not supported')
      return false
    }

    try {
      if (count > 0) {
        await (navigator as any).setAppBadge(count)
        console.log('[PWA] Badge set to:', count)
      } else {
        await (navigator as any).clearAppBadge()
        console.log('[PWA] Badge cleared')
      }
      return true
    } catch (error) {
      console.error('[PWA] Failed to set badge:', error)
      return false
    }
  }

  /**
   * Clear the app badge
   */
  async function clearBadge(): Promise<boolean> {
    return setBadge(0)
  }

  return {
    // State
    needRefresh,
    offlineReady,
    isInstallable,
    isInstalled,
    isStandalone,
    isOnline,

    // Actions
    installApp,
    updateApp,
    dismissUpdate,
    setBadge,
    clearBadge,
  }
}
